import React, { useContext, useEffect, useState } from 'react';
import { Card, Snackbar, Alert, Box } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { OrgContext } from 'context/orgContext';
import { UserContext } from 'context/userContext';
import { useNotifications } from 'context/NotificationContext';
import { userOrgs } from 'services/org';
import { getDeployableProductsFromDB } from 'services/productDataService';

import DeployEnvironment from './components/DeployEnvironment';
import DeployProduct from './components/DeployProduct';
import DeployReview from './components/DeployReview';
import DeployCommit from './components/DeployCommit';

const getProductGroupId = (product) => product.external_id || product.sf_id || product.name;

function Deploy() {
  const [activeStage, setActiveStage] = useState('environment');
  const [isStagesRendered, setIsStagesRendered] = useState(false);
  const { userId } = useContext(UserContext);
  const { orgs, setOrgs } = useContext(OrgContext);
  const { addNotification } = useNotifications();

  const [selectedOrg, setSelectedOrg] = useState(null);
  const [deployableProducts, setDeployableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [versionChoices, setVersionChoices] = useState({});
  const [commitProducts, setCommitProducts] = useState([]);
  const [isReviewCompleted, setIsReviewCompleted] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  useEffect(() => {
    const fetchOrgs = async () => {
      if (!userId) return;
      try {
        const { orgs: f } = await userOrgs();
        setOrgs(f || []);
      } catch (e) {
        console.error(e);
      } finally {
        setIsStagesRendered(true);
      }
    };
    if (!orgs || orgs.length === 0) fetchOrgs();
    else setIsStagesRendered(true);
  }, [userId, orgs, setOrgs]);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedOrg?.org_id) {
        setDeployableProducts([]);
        return;
      }
      const { products, error } = await getDeployableProductsFromDB(selectedOrg.org_id);
      if (error) {
        addNotification({ title: ['Error', 'DB Fetch'], severity: 'error', details: error });
        setDeployableProducts([]);
      } else {
        setDeployableProducts(products);
      }
    };
    fetchProducts();
  }, [selectedOrg, addNotification]);

  // --- HANDLER FUNCTIONS (DEFINITIVE FIX: REMOVED useCallback) ---

  const handleClearSelections = () => {
    setSelectedProducts([]);
    setVersionChoices({});
  };

  const handleEnvSelect = (orgObject) => {
    setSelectedOrg(orgObject);
    handleClearSelections();
  };

  const handleVersionChange = (productGroup, newVersion) => {
    // --- FIX #1: Use the groupId directly from the object ---
    const { groupId, name } = productGroup;
    setVersionChoices((prev) => ({ ...prev, [groupId]: newVersion }));

    const isCurrentlySelected = selectedProducts.some((p) => getProductGroupId(p) === groupId);
    if (isCurrentlySelected) {
      const fullProductForNewVersion = deployableProducts.find(
        (p) => getProductGroupId(p) === groupId && p.product_version === newVersion
      );
      if (fullProductForNewVersion) {
        setSelectedProducts((prev) =>
          prev.map((p) => (getProductGroupId(p) === groupId ? fullProductForNewVersion : p))
        );
        addNotification({
          title: ['Info', 'Version Updated'],
          details: `'${name}' updated to v${newVersion}.`,
          severity: 'info',
        });
      }
    } else {
      addNotification({
        title: ['Info', 'Version Staged'],
        details: `Version for '${name}' set to ${newVersion}. Click row to add.`,
        severity: 'info',
      });
    }
  };

  const handleToggleSelection = (productGroup) => {
    // --- FIX #2: Use the groupId directly from the object ---
    const { groupId, name } = productGroup;
    const isSelected = selectedProducts.some((p) => getProductGroupId(p) === groupId);

    if (isSelected) {
      console.log('[prev]', (prev) => prev.filter((p) => getProductGroupId(p) !== groupId));

      setSelectedProducts((prev) => prev.filter((p) => getProductGroupId(p) !== groupId));
      addNotification({ title: ['Warning', 'Product Removed'], details: `'${name}' removed.`, severity: 'warning' });
    } else {
      const chosenVersion = versionChoices[groupId];
      if (!chosenVersion) {
        addNotification({
          title: ['Action Required', 'Select Version'],
          details: `Select a version for '${name}' first.`,
          severity: 'error',
        });
        return;
      }
      const productToAdd = deployableProducts.find(
        (p) => getProductGroupId(p) === groupId && p.product_version === chosenVersion
      );

      console.log('[productToAdd]', productToAdd);

      if (productToAdd) {
        setSelectedProducts((prev) => [...prev, productToAdd]);

        console.log('[setSelectedProducts]', (prev) => [...prev, productToAdd]);

        addNotification({
          title: ['Success', 'Product Added'],
          details: `'${name}' (v${chosenVersion}) added.`,
          severity: 'success',
        });
      }
    }
  };

  const handleReviewCompletion = (finalProducts) => {
    console.log('[handleReviewCompletion]', finalProducts);
    setCommitProducts(finalProducts);
    setIsReviewCompleted(true);
  };

  const handleStageChange = (stage) => {
    if (stage !== 'environment' && !selectedOrg) {
      setSnackbarMessage('Please select an environment before proceeding.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    setActiveStage(stage);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox display="flex" justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap">
              <ArgonTypography
                variant="button"
                color={activeStage === 'environment' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('environment')}
                sx={{ cursor: 'pointer' }}
              >
                Select Environment
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'products' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('products')}
                sx={{ cursor: selectedOrg ? 'pointer' : 'default', opacity: selectedOrg ? 1 : 0.5 }}
              >
                Select Products
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'review' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('review')}
                sx={{
                  cursor: selectedProducts.length > 0 ? 'pointer' : 'default',
                  opacity: selectedProducts.length > 0 ? 1 : 0.5,
                }}
              >
                Review Changes
              </ArgonTypography>
              <ArgonTypography> → </ArgonTypography>
              <ArgonTypography
                variant="button"
                color={activeStage === 'commit' ? 'info' : 'text'}
                fontWeight="regular"
                onClick={() => handleStageChange('commit')}
                sx={{ cursor: isReviewCompleted ? 'pointer' : 'default', opacity: isReviewCompleted ? 1 : 0.5 }}
              >
                Deploy Changes
              </ArgonTypography>
            </ArgonBox>
          </Card>

          {isStagesRendered && (
            <Box>
              <Box style={{ display: activeStage === 'environment' ? 'block' : 'none' }}>
                <DeployEnvironment envs={orgs || []} selected={selectedOrg} selectedEnv={handleEnvSelect} />
              </Box>
              <Box style={{ display: activeStage === 'products' ? 'block' : 'none' }}>
                <DeployProduct
                  orgs={orgs || []}
                  variants={deployableProducts}
                  selectedProducts={selectedProducts}
                  versionChoices={versionChoices}
                  onVersionChange={handleVersionChange}
                  onToggleSelection={handleToggleSelection}
                  onClearSelections={handleClearSelections}
                />
              </Box>
              <Box style={{ display: activeStage === 'review' ? 'block' : 'none' }}>
                {activeStage === 'review' && (
                  <DeployReview
                    org={selectedOrg}
                    orgId={selectedOrg?.org_id}
                    isRevenue={selectedOrg?.type === 'RC'}
                    selectedProducts={selectedProducts}
                    onReviewComplete={handleReviewCompletion}
                  />
                )}
              </Box>
              <Box style={{ display: activeStage === 'commit' ? 'block' : 'none' }}>
                {activeStage === 'commit' && (
                  <DeployCommit
                    userId={userId}
                    org={selectedOrg}
                    commitProducts={commitProducts}
                    handleStageChange={handleStageChange}
                  />
                )}
              </Box>
            </Box>
          )}
        </ArgonBox>
      </ArgonBox>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} variant="filled" severity={snackbarSeverity} sx={{ alignItems: 'center' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default Deploy;
